import React, { useState } from 'react';
import { CartridgeInfoType } from 'src/components/common/CartridgeForm/Pages/SetCartridgeType/setCartridgeType';
import { ethers } from 'ethers';

import { useBlockchainProvider } from '../Blockchain/blockchainProvider';
import { CARTRIDGE_ABI } from 'src/contracts/token/abi';
import Moralis from 'moralis-v1';
import useCustomTokens from '../CustomTokens/useCustomTokens';
import { cartridges } from 'src/components/common/CartridgeForm/Pages/SetCartridgeType/cartridges';
import { config } from 'src/config';
import axios from 'axios';
import { OPEN_API_URL } from 'src/constants';

interface ICartridge {
	name: string;
	address: string;
	type: CartridgeInfoType;
}

const defaultCartridge: ICartridge = {
	name: 'Sample',
	address: '0x0',
	type: cartridges[0],
};

export enum Stages {
	UNDEPLOYED,
	METADATA_UPLOAD,
	CONFIRMATION,
	DEPLOYING,
	DEPLOYED,
	ALLOWANCE_CONFIRMATION,
	ALLOWING,
	IGO_ALLOWED,
	IGO_CONFIRMATION,
	IG_SETTING,
	IGO_SET,
	REGISTRATING,
	FINISHED,
}

const CARTRIDGE_DECIMALS = 2;

export const CartridgeCreatorProvider = ({ children }: any) => {
	const { contractFactories, contracts, account } = useBlockchainProvider();
	const { addCustomToken } = useCustomTokens();
	const [cartridge, setCartridge] = useState<ICartridge>(defaultCartridge);
	const [stage, setStage] = useState<Stages>(Stages.UNDEPLOYED);
	const [cartridgeType, setCartridgeType] = useState<CartridgeInfoType>(cartridges[0]);
	const [address, setAddress] = useState<string | null>(null);

	const saveMetadata = async (metadataObject: any) => {
		const metadata = await axios.post(`${OPEN_API_URL}/v1/ipfs/uploadJson`, metadataObject);
		console.log(metadata.data.path);
		return (metadata as any).data.path;
	};

	const deploy = async (
		_initialAmount: number,
		_tokenName: string,
		_tokenSymbol: string,
		_feeForCreator: number,
		_metadataObject: string,
		_color: string,
		_logoUrl: string
	) => {
		if (stage === Stages.UNDEPLOYED) {
			setCartridge({ address: '0x1', name: _tokenName, type: cartridges[0] });
			setStage(Stages.METADATA_UPLOAD);

			let _metadataUrl;
			try {
				_metadataUrl = await saveMetadata(_metadataObject);
				console.log(_metadataUrl);
			} catch (error) {
				setStage(Stages.UNDEPLOYED);
				return;
			}

			if (contractFactories.cartridge && contracts.hashupStore) {
				setStage(Stages.CONFIRMATION);
				try {
					// @ts-ignore: Object is possibly 'null'.
					const cartridge = await contractFactories.cartridge.deploy(
						_tokenName,
						_tokenSymbol,
						_metadataUrl,
						(Number(_initialAmount) * Math.pow(10, CARTRIDGE_DECIMALS)).toString(),
						Math.floor(_feeForCreator * 10).toString(),
						config.blockchain.contracts.storeV2
					);
					setStage(Stages.DEPLOYING);

					await cartridge.deployTransaction.wait();
					setAddress(cartridge.address);

					await addCustomToken({
						options: {
							address: cartridge.address,
							decimals: 2,
							image: _logoUrl,
							symbol: _tokenSymbol,
						},
						type: 'ERC20',
					});
					setStage(Stages.DEPLOYED);
				} catch (error) {
					console.log(error);
					setStage(Stages.UNDEPLOYED);
				}
			}
		}
	};

	const allowance = async (amount: number) => {
		if (stage === Stages.DEPLOYED && address) {
			setStage(Stages.ALLOWANCE_CONFIRMATION);

			const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
			const signer = provider.getSigner();
			const CartridgeContract = new ethers.Contract(address, CARTRIDGE_ABI, signer);

			try {
				const tx = await CartridgeContract.approve(
					contracts.hashupStoreV2?.address,
					(Number(amount) * Math.pow(10, CARTRIDGE_DECIMALS)).toString()
				);
				setStage(Stages.ALLOWING);
				await tx.wait();
				setStage(Stages.IGO_ALLOWED);
			} catch (error) {
				console.log(error);
				setStage(Stages.DEPLOYED);
			}
		}
	};

	const setForSale = async (paymentToken: string, price: number, amount: number, marketplaceFee: number) => {
		if (stage === Stages.IGO_ALLOWED && address) {
			setStage(Stages.IGO_CONFIRMATION);
			try {
				const tx = await contracts.hashupStoreV2?.sendLicenseToStore(
					address,
					Number(price) * 10000,
					(Number(amount) * Math.pow(10, CARTRIDGE_DECIMALS)).toString(),
					marketplaceFee
				);
				setStage(Stages.IG_SETTING);
				await tx.wait();
				setStage(Stages.IGO_SET);
			} catch (error) {
				console.log(error);
				setStage(Stages.IGO_ALLOWED);
			}
		}
	};

	const restartUpload = () => {
		setStage(Stages.UNDEPLOYED);
		setAddress(null);
	};

	const register = () => {
		if (stage === Stages.IGO_SET) {
			setStage(Stages.REGISTRATING);

			setTimeout(() => {
				setStage(Stages.FINISHED);
			}, 3000);
		}
	};

	return (
		<CartridgeCreatorContext.Provider
			value={{
				stage,
				deploy,
				register,
				cartridge,
				allowance,
				setForSale,
				cartridgeType,
				restartUpload,
			}}
		>
			{children}
		</CartridgeCreatorContext.Provider>
	);
};

const CartridgeCreatorContext = React.createContext({
	stage: Stages.UNDEPLOYED,
	cartridge: defaultCartridge,
	cartridgeType: cartridges[0],
	allowance: (amount: number) => {},
	register: () => {},
	restartUpload: () => {},
	setForSale: (paymentToken: string, price: number, amount: number, marketplaceFee: number) => {},
	deploy: (
		_initialAmount: number,
		_tokenName: string,
		_tokenSymbol: string,
		_feeForCreator: number,
		_metadataUrl: string,
		_color: string,
		_logoUrl: string
	) => {},
});

export const useCartridgeCreatorProvider = () => {
	const context = React.useContext(CartridgeCreatorContext);
	return context;
};
